import { useState } from 'react';
import { Button, Input, Switch } from '@coordio/ui';
import { CortexDialog, CortexDialogBody, CortexDialogFooter } from '../dialogs/CortexDialog';
import { CodeEditor } from '../common/CodeEditor';
import { toolService } from '../../services/toolService';
import { ToolExecutionResult } from './ToolExecutionResult';
import type { ToolExecutionResult as ToolExecutionResultType, ToolImpactPreview } from '../../types/tool';

export interface ToolExecutionDialogProps {
  open: boolean;
  onClose: () => void;
  toolName: string;
}

/**
 * Dialog for executing a tool with JSON parameters.
 * (Basic implementation: parameters are entered as raw JSON.)
 */
export const ToolExecutionDialog = ({ open, onClose, toolName }: ToolExecutionDialogProps) => {
  const [parametersJson, setParametersJson] = useState<string>('{}');
  const [reason, setReason] = useState<string>('');
  const [isDryRun, setIsDryRun] = useState<boolean>(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [result, setResult] = useState<ToolExecutionResultType | null>(null);

  const parseParameters = (): Record<string, any> => {
    const trimmed = parametersJson.trim();
    if (!trimmed) return {};
    const parsed = JSON.parse(trimmed);
    if (parsed && typeof parsed === 'object' && !Array.isArray(parsed)) return parsed;
    throw new Error('Parameters must be a JSON object');
  };

  const handlePreview = async () => {
    setLoading(true);
    setError(null);
    setResult(null);
    try {
      const parameters = parseParameters();
      const preview: ToolImpactPreview = await toolService.previewTool(toolName, parameters);
      setResult({
        toolName,
        isSuccess: true,
        isDryRun: true,
        result: preview.impactPreviewJson,
        impactPreviewJson: preview.impactPreviewJson,
      });
    } catch (err: any) {
      setError(err?.message || err.response?.data?.message || 'Failed to preview tool');
    } finally {
      setLoading(false);
    }
  };

  const handleExecute = async () => {
    setLoading(true);
    setError(null);
    setResult(null);
    try {
      const parameters = parseParameters();
      const exec = await toolService.executeTool(toolName, parameters, isDryRun, reason || undefined);
      setResult(exec);
    } catch (err: any) {
      setError(err?.message || err.response?.data?.message || 'Failed to execute tool');
    } finally {
      setLoading(false);
    }
  };

  return (
    <CortexDialog open={open} onClose={onClose} title={`Execute Tool: ${toolName}`} size="lg" data-testid="tool-execution-dialog">
      <CortexDialogBody>
        {error && (
          <div className="mb-4 flex items-center justify-between rounded-md border border-red-300 bg-red-50 px-4 py-3 text-sm text-red-800 dark:border-red-700 dark:bg-red-950 dark:text-red-300">
            <span>{error}</span>
            <button type="button" onClick={() => setError(null)} className="ml-2 font-medium hover:underline">✕</button>
          </div>
        )}

        <div className="mb-4 flex flex-col gap-1">
          <label className="text-sm font-medium">Reason (optional)</label>
          <Input
            className="w-full"
            value={reason}
            onChange={(e) => setReason(e.target.value)}
          />
        </div>

        <div className="mb-4">
          <label className="flex items-center gap-2 text-sm font-medium cursor-pointer">
            <Switch checked={isDryRun} onCheckedChange={(checked) => setIsDryRun(checked)} />
            Dry-run (no changes)
          </label>
        </div>

        <div className="mb-2">
          <CodeEditor value={parametersJson} onChange={setParametersJson} language="json" height="200px" />
        </div>

        {loading && (
          <div className="flex justify-center p-4">
            <div className="h-6 w-6 animate-spin rounded-full border-2 border-primary border-t-transparent" />
          </div>
        )}

        <ToolExecutionResult result={result} />
      </CortexDialogBody>
      <CortexDialogFooter>
        <Button variant="ghost" onClick={onClose}>Close</Button>
        <Button onClick={handlePreview} disabled={loading} variant="outline">
          Preview
        </Button>
        <Button onClick={handleExecute} disabled={loading}>
          Execute
        </Button>
      </CortexDialogFooter>
    </CortexDialog>
  );
};
